import { S25Assert } from "../../../util/s25.assert";
import { S25Util } from "../../../util/s25-util";
import { ProgressNode, RequirementGroupOperator, RequirementLeafType } from "./trail.api";
import { TrailLabels } from "./trail.labels";

export function progressStatusLabel(status: string): string {
    switch (status) {
        case "satisfied":
            return TrailLabels.SATISFIED;
        case "in_progress":
            return TrailLabels.IN_PROGRESS;
        case "not_started":
            return TrailLabels.NOT_STARTED;
        case "not_yet_open":
            return TrailLabels.NOT_YET_OPEN;
        case "expired":
            return TrailLabels.EXPIRED;
        case "locked":
            return TrailLabels.LOCKED;
        default:
            return status;
    }
}

export function progressPercent(node: ProgressNode): number {
    if (!node.threshold || node.threshold <= 0) return 0;
    return Math.min(100, ((node.current ?? 0) / node.threshold) * 100);
}

export function progressGroupType(node: ProgressNode): RequirementGroupOperator {
    return node.groupType ?? "ALL";
}

export function progressGroupInstruction(node: ProgressNode): string {
    const groupType = progressGroupType(node);
    switch (groupType) {
        case "ALL":
            return TrailLabels.MUST_COMPLETE_ALL_BELOW;
        case "ANY":
            return TrailLabels.COMPLETE_ANY_BELOW;
        case "N_OF":
            return TrailLabels.COMPLETE_N_OF_BELOW.replace("{n}", formatThreshold(groupCount(node)));
        case "MIN_CREDITS":
            return TrailLabels.EARN_N_CREDITS_BELOW.replace("{n}", formatThreshold(groupCount(node)));
        default:
            return S25Assert.never(groupType, "Unknown group operator");
    }
}

export function progressGroupName(node: ProgressNode): string | undefined {
    const name = node.name?.trim();
    if (!name) return undefined;
    return name;
}

export function progressNodeTitle(node: ProgressNode): string {
    if (node.nodeType === "group") {
        return progressGroupInstruction(node);
    }
    const customName = node.name?.trim();
    if (customName && customName !== node.leafType) return customName;
    return progressLeafTitle(node);
}

function progressLeafTitle(node: ProgressNode): string {
    const leafType = node.leafType;
    const label = node.label?.trim();
    if (leafType === "INCLUDE_TREE" && label) return label;
    if (leafType === "TRAIL_SUMMIT" && label) return label;
    if (isAcademicLeaf(leafType) && label && label !== leafType) return label;
    return leafTypeCopy(leafType);
}

function isAcademicLeaf(leafType: RequirementLeafType | undefined): boolean {
    return leafType === "COURSE" || leafType === "COURSE_RULE" || leafType === "EXTERNAL";
}

function leafTypeCopy(leafType: RequirementLeafType | undefined): string {
    switch (leafType) {
        case "BADGE":
            return TrailLabels.EARN_A_BADGE;
        case "EVENT_ATTENDANCE":
            return TrailLabels.ATTEND_EVENTS;
        case "SERVICE_HOURS":
            return TrailLabels.COMPLETE_SERVICE_HOURS;
        case "EXPERIENCE":
            return TrailLabels.SUBMIT_AN_EXPERIENCE;
        case "TRAIL_SUMMIT":
            return TrailLabels.COMPLETE_A_TRAIL;
        case "APPROVAL":
            return TrailLabels.ADVISOR_APPROVAL;
        case "ORG_ROLE":
            return TrailLabels.HOLD_AN_ORG_ROLE;
        case "INCLUDE_TREE":
            return TrailLabels.INCLUDED_REQUIREMENTS;
        case "COURSE":
            return "Course";
        case "COURSE_RULE":
            return "Course rule";
        case "EXTERNAL":
            return "External requirement";
        case undefined:
            return "";
        default:
            return S25Assert.never(leafType, "Unknown leaf type");
    }
}

function groupCount(node: ProgressNode): number {
    return node.threshold != null ? S25Util.toFloat(node.threshold) : 0;
}

function formatThreshold(value: number): string {
    if (!Number.isFinite(value)) return "0";
    if (Number.isInteger(value)) return String(value);
    return String(value);
}
